import { LOCALE } from "./constants";
import {
  CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
  CareerPreferenceRank,
  ROMANTIC_COMPATIBILITY_TRAIT_KEY_MAP,
  traitNameKeyMap,
  traitTitleKeyMap,
} from "./general-config";
import { likeabilityTraitColors, snakeToTitleCase } from "./utility-functions";

export const getPanelData = ({ isAssessmentEnabled, level, panels }) => {
  if (!isAssessmentEnabled || typeof level !== "string") return null;
  return (
    panels.find(
      (panel) =>
        typeof panel?.nameEnglishCode === "string" &&
        panel.nameEnglishCode.toLowerCase() === level.toLowerCase()
    ) || null
  );
};

export const getEmotioanlIntelligenceScoreLevel = (score) => {
  const ranges = [
    { label: "High", min: 41, max: 50 },
    { label: "Moderate", min: 26, max: 40 },
    { label: "Low", min: 10, max: 25 },
  ];

  for (const { min, max, label } of ranges) {
    if (score >= min && score <= max) {
      return label;
    }
  }

  return null;
};

export const getEmotioanlIntelligenceOverAllScoreLevel = (score) => {
  const ranges = [
    { label: "High", min: 200, max: 250 },
    { label: "Moderate", min: 125, max: 199 },
    { label: "Low", min: 50, max: 124 },
  ];

  for (const { min, max, label } of ranges) {
    if (score >= min && score <= max) {
      return label;
    }
  }

  return null;
};

export const getEmotioanlIntelligenceTraitScoresWithTotal = (userTraits) => {
  const totalScore = userTraits.reduce(
    (sum, item) => sum + (item?.score || 0),
    0
  );
  return totalScore;
};

export const transformNarcissisticPersonalityUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
        updatedScore: item.score * 10,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return {
    result,
    totalScore,
    totalPercentage: (totalScore * 100) / 250,
  };
};

export const getNarcissisticPersonalityLevel = (score, t) => {
  if (10 <= score && score < 20)
    return {
      englishCode: "Low",
      localizedCode: t("report.narcissistic_personality.score_level.low"),
    };
  if (20 <= score && score < 30)
    return {
      englishCode: "Moderate",
      localizedCode: t("report.narcissistic_personality.score_level.moderate"),
    };
  if (30 <= score && score < 40)
    return {
      englishCode: "High",
      localizedCode: t("report.narcissistic_personality.score_level.high"),
    };
  if (40 <= score && score <= 50)
    return {
      englishCode: "Very High",
      localizedCode: t("report.narcissistic_personality.score_level.very_high"),
    };
  return null;
};

export const getOverAllNarcissisticPersonalityLevel = (score, t) => {
  return getNarcissisticPersonalityLevel(score / 5, t);
};

export const getContentData = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  return data[0]?.content;
};

export const calculateLikeabilityTotalScore = (traits) => {
  if (!Array.isArray(traits)) return 0;

  return traits.reduce((total, trait) => {
    return total + (trait?.score || 0);
  }, 0);
};
export const getLikeabilityScoreCategory = (score) => {
  if (score >= 151) return { category: "High", index: 0 };
  else if (score >= 101) return { category: "Moderate", index: 1 };
  else return { category: "Low", index: 2 };
};

export const getHighlySensitiveLevel = (totalScore) => {
  if (typeof totalScore !== "number") return "low_sensitivity";

  if (totalScore >= 40 && totalScore <= 80) {
    return "low_sensitivity";
  } else if (totalScore >= 81 && totalScore <= 120) {
    return "moderate_sensitivity";
  } else if (totalScore >= 121 && totalScore <= 160) {
    return "high_sensitivity";
  } else if (totalScore >= 161 && totalScore <= 200) {
    return "very_high_sensitivity";
  }

  return "low_sensitivity"; // Fallback
};

export const transformHighlySensitiveUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return { result, totalScore };
};

export const transformDifficultPersonUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return { result, totalScore: totalScore / 4 };
};

export const getDifficultyLevel = (score, type) => {
  if (typeof score !== "number") return "low_difficulty"; // Default if score is missing or invalid

  if (type === "subscale") {
    if (score > 4 && score <= 5) return "very_high_difficulty";
    if (score > 3 && score <= 4) return "high_difficulty";
    if (score > 2 && score <= 3) return "moderate_difficulty";
    return "low_difficulty";
  } else if (type === "overall") {
    if (score > 16 && score <= 20) return "very_high_difficulty";
    if (score > 12 && score <= 16) return "high_difficulty";
    if (score > 8 && score <= 12) return "moderate_difficulty";
    return "low_difficulty";
  }

  return "low_difficulty"; // Fallback case
};

// Calculate total score from all traits
export const calculateTotalScoreDepression = (traits) => {
  if (!Array.isArray(traits) || traits.length === 0) return 0;
  return traits.reduce((sum, trait) => sum + (trait?.score || 0), 0);
};
// Map total score to depression level (for overall results)
export const getDepressionLevel = (totalScore) => {
  if (totalScore >= 40 && totalScore <= 64) {
    return "minimal";
  } else if (totalScore >= 65 && totalScore <= 94) {
    return "mild";
  } else if (totalScore >= 95 && totalScore <= 129) {
    return "moderate";
  } else if (totalScore >= 130 && totalScore <= 159) {
    return "severe";
  } else if (totalScore >= 160 && totalScore <= 200) {
    return "very_severe";
  }
  return null;
};

// Function to find the trait with the highest percentage in userTraits
export const findColorBlingHighestPercentageTrait = (userTraits) => {
  if (!Array.isArray(userTraits) || userTraits.length === 0) {
    return null;
  }

  return userTraits.reduce((highest, current) => {
    if (typeof current?.percentage !== "number") {
      return highest;
    }

    if (!highest || current.percentage > highest.percentage) {
      return current;
    }

    return highest;
  }, null);
};
export const calculateBipolarScore = (userTraits) => {
  if (!userTraits || userTraits.length === 0) return 0;

  // Calculate average score from all traits
  const totalScore = userTraits.reduce(
    (sum, trait) => sum + (trait.score || 0),
    0
  );
  const averageScore = totalScore / userTraits.length;

  return averageScore;
};

// Function to get result category based on score
export const getBipolarResultCategory = (score) => {
  if (score >= 1 && score <= 2.19) {
    return "VeryLowLikelihood";
  } else if (score >= 2.2 && score <= 3.39) {
    return "LowLikelihood";
  } else if (score >= 3.4 && score <= 4.59) {
    return "ModerateLikelihood";
  } else if (score >= 4.6 && score <= 5.79) {
    return "HighLikelihood";
  } else if (score >= 5.8 && score <= 7) {
    return "VeryHighLikelihood";
  }
};

export const getAustismSpecturmOverAllLevelDetails = (score) => {
  if (score >= 40 && score <= 99) {
    return {
      level: "low",
    };
  } else if (score >= 100 && score <= 149) {
    return {
      level: "moderate",
    };
  } else if (score >= 150 && score <= 200) {
    return {
      level: "high",
    };
  } else {
    return {
      level: "Invalid",
      details: "Score is out of expected range (10–50).",
    };
  }
};

export const transformAutismSpectrumUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return { result, totalScore };
};

export const getAntisocialPersonalityDisorderLevel = (score, t) => {
  if (1 <= score && score <= 2)
    return {
      englishCode: "Low",
      localizedCode: t("report.narcissistic_personality.score_level.low"),
    };
  if (2 < score && score <= 3)
    return {
      englishCode: "Moderate",
      localizedCode: t("report.narcissistic_personality.score_level.moderate"),
    };
  if (3 < score && score <= 4)
    return {
      englishCode: "High",
      localizedCode: t("report.narcissistic_personality.score_level.high"),
    };
  if (4 < score && score <= 5)
    return {
      englishCode: "Very High",
      localizedCode: t("report.narcissistic_personality.score_level.very_high"),
    };
  return null;
};

export const transformAntisocialPersonalityDisorderUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
        updatedScore: item.score * 10,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return {
    result,
    totalScore,
    updatedTotalScore: totalScore * 10,
    avgTotalScore: totalScore / data.length,
  };
};

export const getAdhdScoreCategory = (score) => {
  if (typeof score !== "number") return null;

  if (score > 4 && score <= 5) return "very";
  if (score > 3 && score <= 4) return "high";
  if (score > 2 && score <= 3) return "moderate";
  if (score >= 1 && score <= 2) return "low";

  return null;
};

export const getAdhdDetailWithLabel = (currentUserTraits) => {
  return currentUserTraits.map((item) => ({
    traitId: item.traitId,
    trait: item.trait?.title,
    score: getAdhdScoreCategory(item.score),
    percentage: item.percentage,
  }));
};

export const transformAdhdUserTraitsData = (data) => {
  if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

  const result = data.reduce((acc, item) => {
    if (
      item?.trait?.title &&
      typeof item.percentage === "number" &&
      typeof item.score === "number"
    ) {
      acc[item.trait.title] = {
        percentage: item.percentage,
        score: item.score,
      };
    }
    return acc;
  }, {});

  const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

  return { result, totalScore };
};

const BIG_FIVE_DOMAINS = ["O", "C", "E", "A", "N"];

// Common score ranges for Big Five trait scoring
export const BIG_FIVE_SCORE_RANGES = [
  { upper: 5, lower: 4.6, suffix: "_H" },
  { upper: 4.6, lower: 3.53, suffix: "_AA" },
  { upper: 3.53, lower: 2.47, suffix: "_A" },
  { upper: 2.47, lower: 1.4, suffix: "_BA" },
  { upper: 1.4, lower: 1, suffix: "_L" },
];

export const getBigFiveTraitResultCode = (score, traitCode) => {
  if (!score || !traitCode || score < 1 || score > 5) {
    return "Invalid input";
  }

  // const validTraits = new Set(["E", "A", "O", "C", "N"]);
  // if (!validTraits.has(traitCode)) {
  //   return `Invalid trait code: ${traitCode}`;
  // }
  // Adjust the range check to include `score === 1`
  const matchingRange = BIG_FIVE_SCORE_RANGES.find((range) =>
    range.lower === 1
      ? score <= range.upper && score >= range.lower
      : score <= range.upper && score > range.lower
  );

  // const matchingRange = scoreRanges.find(
  //   (range) => score <= range.upper && score > range.lower
  // );

  return matchingRange
    ? `${traitCode}${matchingRange.suffix}`
    : `No matching result found for score: ${score}`;
};

export const BIG_FIVE_TRAITS_LONG_NAME = {
  E: "Extraversion",
  O: "Openness",
  A: "Agreeableness",
  C: "Conscientiousness",
  N: "Neuroticism",
};
// Score level label mapping for suffixes
export const getBigFiveScoreLevelLabels = (t) => ({
  _H: `${t("report.bigfive.level.high")}`,
  _AA: `${t("report.bigfive.level.above_average")}`,
  _A: `${t("report.bigfive.level.average")}`,
  _BA: `${t("report.bigfive.level.below_average")}`,
  _L: `${t("report.bigfive.level.low")}`,
});

// -------------- Get trait labels from userTraits (short version for dashboard) --------------
export const getBigFiveTraitDetailsWithLabels = (userTraits, t) => {
  if (!userTraits || userTraits.length === 0) return [];
  const BIG_FIVE_SCORE_LEVEL_LABELS = getBigFiveScoreLevelLabels(t);

  return userTraits.map((item) => {
    const { score, trait } = item;
    const { name, title } = trait;
    const resultCode = getBigFiveTraitResultCode(score, title);
    const suffix = resultCode?.slice(-2); // "_A"
    return {
      trait: BIG_FIVE_TRAITS_LONG_NAME[title] || name || title,
      level: suffix ? BIG_FIVE_SCORE_LEVEL_LABELS[suffix] : "Unknown",
      score,
      label: t(`dashboard.bigfive_trait_type.${title.toLowerCase()}`),
    };
  });
};
export const calculateBigFiveFromFacets = (currentUserTraits = [], t) => {
  // initialize buckets
  const domainMap = {
    O: { traits: [], totalScore: 0 },
    C: { traits: [], totalScore: 0 },
    E: { traits: [], totalScore: 0 },
    A: { traits: [], totalScore: 0 },
    N: { traits: [], totalScore: 0 },
  };
  const BIG_FIVE_SCORE_LEVEL_LABELS = getBigFiveScoreLevelLabels(t);

  // single pass over all 36 facets
  currentUserTraits.forEach((item) => {
    const domain = item.trait?.title?.charAt(0);
    if (!BIG_FIVE_DOMAINS.includes(domain)) return;

    domainMap[domain].traits.push(item);
    domainMap[domain].totalScore += item.score;
  });

  // calculate avg score + level
  return Object.entries(domainMap).map(([domain, data]) => {
    const averageScore =
      data.traits.length === 6
        ? data.totalScore / 6
        : data.traits.length
          ? data.totalScore / data.traits.length
          : 0;

    return {
      trait: BIG_FIVE_TRAITS_LONG_NAME[domain],
      averageScore: Number(averageScore.toFixed(2)),
      resultCode: getBigFiveTraitResultCode(averageScore, domain),
      traits: data.traits, // full facet data if UI needs it
      level:
        BIG_FIVE_SCORE_LEVEL_LABELS[
          getBigFiveTraitResultCode(averageScore, domain).slice(-2)
        ],
      label: t(`dashboard.bigfive_trait_type.${domain.toLowerCase()}`),
    };
  });
};

export const generateContentKeyForCareerPreference = (
  USER_TRAITS,
  assessmentId,
  currentLocale
) => {
  if (!Array.isArray(USER_TRAITS) || USER_TRAITS.length === 0) return "";

  const isCareerTest =
    typeof assessmentId === "string" &&
    assessmentId.toLowerCase() ===
      CAREER_PREFERENCE_TEST_ASSESSMENT_ID.toLowerCase();

  if (!isCareerTest) return "";
  const localeKey =
    traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
  const englishKey = traitTitleKeyMap[LOCALE.EN];

  // Sort traits by score descending, then by CareerPreferenceRank ascending
  const sortedTraits = [...USER_TRAITS].sort((a, b) => {
    const scoreDiff = b.score - a.score;
    if (scoreDiff !== 0) return scoreDiff;

    // If scores are equal, sort by rank
    return (
      CareerPreferenceRank[a.trait.title] - CareerPreferenceRank[b.trait.title]
    );
  });

  // Extract top 4 traits
  // const topFour = sortedTraits.slice(0, 4).map((t) => t.trait.title);
  const topFour = sortedTraits.slice(0, 4).map((t) => ({
    en: t.trait[englishKey],
    localized: t.trait[localeKey],
  }));

  // Return structured result
  return {
    primary: topFour[0] || null,
    secondary: topFour.slice(1),
  };
};

export const getFourTemperamentsDetailsWithLabel = (
  userTraits = [],
  currentLocale
) => {
  const localeKey =
    traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
  return userTraits.map((item) => ({
    traitId: item.traitId,
    trait: snakeToTitleCase(item.trait[localeKey]),
    score: item.score,
    percentage: item.percentage,
  }));
}; //check report page

export const getLikeabilityDetailsWithLabel = (userTraits = []) => {
  const totalScore = userTraits?.reduce((accumulator, trait) => {
    return accumulator + trait.score;
  }, 0);
  return totalScore;
};

export const romanticCompatibilityPersonalizedLevelSuffix = (percentage) => {
  if (percentage >= 80) return "H";
  if (percentage >= 61) return "M";
  return "L";
};

export const getRomanticCompatiblityDetailsWithLabel = (
  userTraits = [],
  currentLocale,
  t
) => {
  const localeKey =
    traitNameKeyMap[currentLocale] || traitNameKeyMap[LOCALE.EN];
  const Level_Label = {
    H: `${t("report.romantic-personalized.level.h")}`,
    M: `${t("report.romantic-personalized.level.m")}`,
    L: `${t("report.romantic-personalized.level.l")}`,
  };

  return userTraits.map((item) => ({
    traitId: item.traitId,
    trait: ROMANTIC_COMPATIBILITY_TRAIT_KEY_MAP[item.trait?.title],
    score: item.score,
    percentage: item.percentage,
    label: item.trait[localeKey],
    level:
      Level_Label[
        romanticCompatibilityPersonalizedLevelSuffix(item.percentage)
      ], // ⭐ using your helper
  }));
};

export const personalityDisorderDetermineTraitLevel = (score) => {
  if (3.67 < score && score <= 5)
    return {
      en: "High",
      es: "Alto",
    };
  if (2.33 < score && score <= 3.67)
    return {
      en: "Moderate",
      es: "Moderado",
    };
  if (0 <= score && score <= 2.33) return { en: "Low", es: "Bajo" };
};

export const getPersonalityDisorderDetailWithLabel = (
  userTraits = [],
  currentLocale = "en"
) => {
  const localeKey =
    traitNameKeyMap[currentLocale] || traitNameKeyMap[LOCALE.EN];
  return userTraits.map((item) => {
    const levelObj = personalityDisorderDetermineTraitLevel(item.score);

    return {
      traitId: item.traitId,
      trait: item.trait?.title,
      score: item.score,
      percentage: item.percentage,
      level: levelObj?.[currentLocale] ?? levelObj?.en ?? "",
      label: snakeToTitleCase(item.trait?.[localeKey] || ""),
    };
  });
};

export const getLoveStyleDetailsWithLabel = (
  userTraits = [],
  currentLocale
) => {
  if (!userTraits.length) return null;
  const localeKey =
    traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
  // Sort by percentage (highest first)
  const sortedTraits = [...userTraits].sort(
    (a, b) => b.percentage - a.percentage
  );

  const mainTrait = sortedTraits[0];

  return {
    mainLoveStyle: mainTrait.trait.title,
    scores: sortedTraits.map((t) => ({
      label: snakeToTitleCase(t.trait[localeKey]),
      percentage: t.percentage,
    })),
  };
};

export const DYSLEXIA_ADULT_SCORE_LEVEL_RANGES = {
  Low: { min: 1, max: 9 },
  Moderate: { min: 10, max: 19 },
  High: { min: 20, max: Infinity },
};

export const getDyslexiaAdultLevelFromScore = (score) => {
  for (const level in DYSLEXIA_ADULT_SCORE_LEVEL_RANGES) {
    const { min, max } = DYSLEXIA_ADULT_SCORE_LEVEL_RANGES[level];
    if (score >= min && score <= max) return level;
  }
  return "Unknown";
};

export const getDyslexiaAdultDetailsWithLabel = (
  userTraits = [],
  currentLocale,
  t
) => {
  const localeKey =
    traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
  return userTraits.map((item) => ({
    traitId: item.traitId,
    trait: item.trait?.title,
    score: item.score,
    percentage: item.percentage,
    level: `${t(`report.dyslexia_adult.level.${getDyslexiaAdultLevelFromScore(item.score).toLowerCase()}`)}`,
    label: snakeToTitleCase(item.trait[localeKey]), // Assuming you want to use the trait title as the label
  }));
};

const DYSLEXIA_CHILDREN_SCORE_LEVEL_RANGES = {
  Unlikely: { min: 10, max: 20 },
  Mild: { min: 21, max: 30 },
  Moderate: { min: 31, max: 40 },
  Strong: { min: 41, max: 50 },
};

export const getDyslexiaChildrenLevelFromScore = (score) => {
  for (const level in DYSLEXIA_CHILDREN_SCORE_LEVEL_RANGES) {
    const { min, max } = DYSLEXIA_CHILDREN_SCORE_LEVEL_RANGES[level];
    if (score >= min && score <= max) return level;
  }
  return "Unknown";
};

export const getDyslexiaChildrenDetailsWithLabel = (
  userTraits = [],
  currentLocale,
  t
) => {
  const localeKey =
    traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
  return userTraits.map((item) => ({
    traitId: item.traitId,
    trait: item.trait?.title,
    score: item.score,
    percentage: item.percentage,
    level: `${t(`report.dyslexia_children.level.${getDyslexiaChildrenLevelFromScore(item.score).toLowerCase()}`)}`,
    label: snakeToTitleCase(item.trait[localeKey]), // Assuming you want to use the trait title as the label
  }));
};

export function getDiscPersonalityTopTwoTraitsByScore(data) {
  data?.sort((a, b) => b.score - a.score);
  return data.slice(0, 2).map((item) => item.trait.title);
}

export const DISC_LABEL = {
  I: "Influence (I),",
  C: "Conscientiousness (C).",
  D: "Dominance (D)",
  S: "Steadiness (S)",
};
const replacePlaceholders = (template, values) => {
  return template.replace(/{(.*?)}/g, (_, key) => values[key] ?? "");
};

export const getDiscPersonalityDetailsWithLabel = (userTraits, content) => {
  const topTwoTraits = getDiscPersonalityTopTwoTraitsByScore(userTraits);

  if (!topTwoTraits || topTwoTraits.length < 2 || !content) return "";

  const [primary, secondary] = topTwoTraits;

  return replacePlaceholders(content, {
    primary: DISC_LABEL[primary],
    secondary: DISC_LABEL[secondary],
  });
};

export const attachmentStyleScore = (userTraits, currentLocale) => {
  if (!Array.isArray(userTraits)) return [];
  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  return userTraits.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];

    return {
      label: snakeToTitleCase(item?.trait?.[localeKey]),
      score: item?.score,
      percentage: item?.percentage,
      clr: progressBarColor[index % progressBarColor.length],
    };
  });
};

export const likeabilityScore = (userTraits, currentLocale) => {
  if (!Array.isArray(userTraits)) return [];

  const localeKey =
    traitNameKeyMap[currentLocale] || traitNameKeyMap[LOCALE.EN];

  return userTraits.map(({ trait, percentage }) => ({
    label: trait?.[localeKey] ?? "",
    percentage: percentage ?? 0,
    color: likeabilityTraitColors?.[trait?.title] || "rgba(0, 0, 0, 1)",
  }));
};

export const getTraitCombinations = (traits) => {
  if (!Array.isArray(traits) || traits.length === 0) {
    return [];
  }

  const titles = traits
    .map((t) => t?.trait?.title)
    .filter(Boolean)
    .map((title) => title.charAt(0).toUpperCase() + title.slice(1));

  const result = [];

  function generatePermutations(arr, currentCombo = []) {
    if (arr.length === 0) {
      result.push(currentCombo.join("-"));
      return;
    }
    for (let i = 0; i < arr.length; i++) {
      generatePermutations(
        [...arr.slice(0, i), ...arr.slice(i + 1)],
        [...currentCombo, arr[i]]
      );
    }
  }

  generatePermutations(titles);
  return result;
};

export const fourTemperamentTopUserTraitsArray = (userTraits) => {
  if (!Array.isArray(userTraits) || userTraits.length === 0) return [];
  const sortedTraits = [...(userTraits || [])].sort(
    (a, b) => b?.score - a?.score
  );
  const topScore = sortedTraits[0]?.score ?? -1;
  return sortedTraits.filter((trait) => topScore - trait?.score <= 2);
};

export const fourTemperamentTraitCombination = (userTraits) => {
  const topTraits = fourTemperamentTopUserTraitsArray(userTraits);
  return getTraitCombinations(topTraits);
};

export const fourTemperamentFindPersonalityBlend = (
  topTraitArray,
  contentData
) => {
  const selectedTraitNames = topTraitArray.map((item) => item);
  return contentData.find((item) =>
    selectedTraitNames.includes(item.nameEnglishCode)
  );
};
